import { GoogleSigninButtonModule, SocialAuthService } from "@abacritt/angularx-social-login";
import { isPlatformBrowser, NgIf } from "@angular/common";
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Inject, OnDestroy, OnInit, Output, PLATFORM_ID, signal, ViewChild, WritableSignal } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { GoogleLogoSvgComponent } from "@src/app/components/svg/google-logo/google-logo.component";
import { TwitterLogoSvgComponent } from "@src/app/components/svg/twitter-logo/twitter-logo.component";
import { AuthService } from "@src/app/servicios/auth.service";
import { CookieService } from "ngx-cookie-service";
import { first, Subscription } from "rxjs";
import { ConfigService } from "src/app/servicios/config/config.service";
import { PlatformService } from "src/app/servicios/platform/platform.service";
import { Md5 } from "ts-md5";
import { environment } from "../../../environments/environment";
import { ModalRegistroComponent } from "../modal-registro/modal-registro.component";

@Component({
  selector: "app-modal-login",
  templateUrl: "./modal-login.component.html",
  styleUrls: ["./modal-login.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, ReactiveFormsModule, GoogleSigninButtonModule],
})
export class ModalLoginComponent implements OnInit, OnDestroy, AfterViewInit {
  GoogleLogoSvgComponent = GoogleLogoSvgComponent;
  TwitterLogoSvgComponent = TwitterLogoSvgComponent;

  required = Validators.required;
  emailFormControl = new FormControl("", [Validators.required, Validators.email]);
  passwordFormControl = new FormControl("", [Validators.required]);
  loginForm: FormGroup;
  constantesGlobales: any;
  isSocialLogin = signal(false);
  googleClientId: WritableSignal<string> = signal("");
  googleCallbackUrl: WritableSignal<string> = signal("");
  twitterClientId: WritableSignal<string> = signal("");
  twitterClientSecret: WritableSignal<string> = signal("");
  googleLoginSub: Subscription;

  @Output() closeModal = new EventEmitter<void>();
  @ViewChild("darkBackground") darkBackground!: ElementRef;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly authService: AuthService,
    private readonly cookieService: CookieService,
    private readonly router: Router,
    private readonly _snackBar: MatSnackBar,
    private readonly configService: ConfigService,
    public dialog: MatDialog,
    @Inject(PLATFORM_ID) private readonly platformId,
    private readonly platformService: PlatformService,
    private readonly socialAuth: SocialAuthService,
    private elRef: ElementRef,
  ) {
    this.loginForm = this.formBuilder.group({
      email: this.emailFormControl,
      password: this.passwordFormControl,
    });
  }

  ngOnInit(): void {
    this.constantesGlobales = this.configService.getConstantesGlobales();
    if (this.socialAuth.initState) {
      this.socialAuth.initState.pipe(first()).subscribe((initialized) => {
        if (!initialized) return;
        this.initializeSocialVariables();
      });
    }
  }

  ngAfterViewInit(): void {
    if (this.router.url === "/login") {
      this.darkBackground.nativeElement.classList.remove("black-background");
      this.darkBackground.nativeElement.classList.add("no-background");
    }
  }

  initializeSocialVariables() {
    const configPrensa = this.configService.getConfigPrensa();
    if (configPrensa) {
      configPrensa.pipe(first()).subscribe((config) => {
        const globalConfig = config["general"]["global-constants"];
        if (!globalConfig) return;

        const socialLoginConfig = globalConfig["social-login"];
        if (!socialLoginConfig) return;

        this.googleClientId.set(globalConfig["social-login-google-client-id"]);
        this.googleCallbackUrl.set(globalConfig["social-login-google-callback-url"]);

        const isGoogleEnabled = Boolean(this.googleClientId && this.googleCallbackUrl);

        if (isGoogleEnabled) this.initializeGoogleSubscription();

        this.twitterClientId.set(globalConfig["social-login-twitter-client-id"]);
        this.twitterClientSecret.set(globalConfig["social-login-twitter-client-secret"]);
        const isTwitterEnabled = Boolean(this.twitterClientId && this.twitterClientSecret);

        this.isSocialLogin.set(isGoogleEnabled || isTwitterEnabled);
      });
    }
  }

  initializeGoogleSubscription() {
    this.googleLoginSub = this.socialAuth.authState.subscribe((user) => {
      if (user) {
        const googleLogin = this.authService.sendGoogleLogin(user.idToken);
        if (googleLogin) {
          googleLogin.pipe(first()).subscribe({
            next: (result) => {
              if (!result) return;
              if (result.message) {
                this._snackBar.open(result.message, "", {
                  duration: 2000,
                  horizontalPosition: "center",
                  verticalPosition: "top",
                  panelClass: ["snackbar-success"],
                });
              }
              if (this.platformService.isBrowser()) {
                this.setUserCookie(result.data, 60 * 60 * 24 * 30);
              }
              if (this.router.url === "/login") {
                if (isPlatformBrowser(this.platformId)) {
                  window.location.replace("/");
                }
              } else {
                if (isPlatformBrowser(this.platformId)) {
                  window.location.reload();
                }
              }
            },
            error: (response) => {
              this._snackBar.open(response.error.message, "OK", {
                duration: 2000,
                horizontalPosition: "center",
                verticalPosition: "top",
                panelClass: ["snackbar-error"],
              });
            },
          });
        }
      }
    });
  }

  /**
   *
   * @returns void
   * Función para enviar el formulario de login
   */
  sendLogin(): void {
    const formValue = this.loginForm.value;
    const email = formValue.email;
    const password = formValue.password;

    if (!email || !password) {
      this.showSnackbar("Por favor, completa todos los campos", "OK", "snackbar-error");
      return;
    }

    const md5Password = new Md5().appendStr(password).end();
    const login = this.authService.sendLogin(email, String(md5Password));

    if (!login) return;

    login.pipe(first()).subscribe({
      next: (result) => this.handleLoginSuccess(result),
      error: () => this.showSnackbar("El usuario o la contraseña es incorrecto", "OK", "snackbar-error"),
    });
  }

  private handleLoginSuccess(result: any): void {
    if (!result) {
      this.showSnackbar("El usuario o la contraseña es incorrecto", "OK", "snackbar-error");
      return;
    }

    if (this.platformService.isBrowser()) {
      this.setUserCookie(result);
    }

    if (this.router.url === "/login") {
      this.reloadPage("/");
    } else {
      this.reloadPage();
    }
  }

  private reloadPage(url?: string): void {
    if (isPlatformBrowser(this.platformId)) {
      if (url) {
        window.location.replace(url);
      } else {
        window.location.reload();
      }
    }
  }

  private showSnackbar(message: string, action: string, panelClass: string): void {
    this._snackBar.open(message, action, {
      duration: 3000,
      horizontalPosition: "center",
      verticalPosition: "top",
      panelClass: [panelClass],
    });
  }

  /**
   * Función para guardar el usuario en una cookie
   *
   * @param {any} value - Usuario a guardar
   * @param {number} expiration - Tiempo de expiración de la cookie. Por defecto 30 días.
   * @returns void
   */
  setUserCookie(value, expiration = 60 * 60 * 24 * 30): void {
    if (!this.platformService.isBrowser()) return;

    this.cookieService.set("userLogin" + environment.name, JSON.stringify(value), expiration);
  }

  openRegistroDialog(): void {
    this.dialog.open(ModalRegistroComponent, {
      panelClass: "modal-register",
    });
    this.onCloseModal();
  }

  onCloseModal() {
    this.closeModal.emit();
  }

  ngOnDestroy(): void {
    if (this.googleLoginSub) this.googleLoginSub.unsubscribe();
  }
}
